import { LoadingOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Table,
  TablePaginationConfig,
} from "antd";
import {
  ColumnsType,
  FilterValue,
  SorterResult,
} from "antd/lib/table/interface";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import {
  getAdminEmails,
  getAdminExcel,
  getAdminPaginatedDocuments,
} from "../../../services/adminApi";
import {
  endDateValue,
  startDocumentDateValue,
} from "../../../shared/constants/Resources";
import AdminDTO from "../../../shared/dtos/adminDTO";
import "../../../shared/styles/list.css";
import "./styles.css";
import ContainerLayout from "../../common/containerLayout";

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
  filters: Record<string, FilterValue | null>;
}

export interface DataType {
  id: number;
  url: string;
  fileKey: string;
}

const AdminReportDocumentList = () => {
  const title = "Documentos de compañias";
  const [form] = Form.useForm<any>();
  const [modalForm] = Form.useForm<any>();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [emails, setEmails] = useState<AdminDTO[]>([]);
  const [checkedEmails, setCheckedEmails] = useState<number[]>([]);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [checkAll, setCheckAll] = useState(true);
  const [search, setSearch] = useState("");
  const [emailSearch, setEmailSearch] = useState<AdminDTO[]>([]);

  Form.useWatch("startDate", form);
  Form.useWatch("endDate", form);
  Form.useWatch("search", form);
  Form.useWatch("startDate", modalForm);
  Form.useWatch("endDate", modalForm);

  const columns: ColumnsType<DataType> = [
    {
      title: "Nombre del archivo",
      dataIndex: "fileName",
      sorter: false,
      width: "20%",
    },
    {
      title: "Url",
      dataIndex: "url",
      width: "40%",
      render: (url) => (
        <a href={url} target="_blank" rel="noreferrer">
          {url}
        </a>
      ),
    },
    {
      title: "Nombre de la empresa",
      dataIndex: "adminName",
      width: "40%",
    },
  ];

  const [tableParams, setTableParams] = useState<TableParams>({
    filters: {},
    pagination: {
      current: 1,
      pageSize: 10,
    },
  });

  const fetchData = async () => {
    setLoading(true);

    const startDate = form.getFieldValue("startDate");
    const endDate = form.getFieldValue("endDate");
    const startDateModal = modalForm.getFieldValue("startDate");
    const endDateModal = modalForm.getFieldValue("endDate");

    if (!startDateModal && !endDateModal)
      modalForm.setFieldsValue({
        startDate: dayjs(startDocumentDateValue),
        endDate: dayjs(endDateValue),
      });

    if (!startDate && !endDate)
      form.setFieldsValue({
        startDate: dayjs(startDocumentDateValue),
        endDate: dayjs(endDateValue),
      });

    const { data, total } = await getAdminPaginatedDocuments({
      page: tableParams.pagination?.current || 1,
      take: tableParams.pagination?.pageSize || 10,
      ...form.getFieldsValue(true),
    });

    const results: Array<AdminDTO> = await getAdminEmails(
      modalForm.getFieldsValue(true),
    );

    setData(data);
    setLoading(false);
    setEmails(results);
    setCheckedEmails(results.map((admin: any) => admin.admin_id));
    setTableParams({
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        total,
      },
    });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(tableParams)]);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<DataType> | SorterResult<DataType>[],
  ) => {
    setTableParams({
      pagination,
      filters,
      ...sorter,
    });
  };

  const handleClick = async () => {
    const startDate = form.getFieldValue("startDate");
    const endDate = form.getFieldValue("endDate");
    if (!startDate && !endDate) return;

    setLoading(true);

    const { data, total } = await getAdminPaginatedDocuments({
      page: tableParams.pagination?.current || 1,
      take: tableParams.pagination?.pageSize || 10,
      ...form.getFieldsValue(true),
    });

    setData(data);
    setLoading(false);
    setTableParams({
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        total,
      },
    });
  };

  const handleClickDate = async () => {
    const startDateModal = modalForm.getFieldValue("startDate");
    const endDateModal = modalForm.getFieldValue("endDate");
    if (!startDateModal && !endDateModal) return;

    setIsModalLoading(true);
    setEmails([]);

    const results = await getAdminEmails(modalForm.getFieldsValue(true));

    setIsModalLoading(false);
    setEmails(results);
    setCheckAll(true);
    setCheckedEmails(
      results.map((admin: any) => {
        return admin.admin_id;
      }),
    );
  };

  const showModal = () => {
    setIsModalOpen(true);
    setCheckAll(true);
    setCheckedEmails(emails.map((admin: any) => admin.admin_id));
  };

  const handleOk = async () => {
    setConfirmLoading(true);
    try {
      for (const email of checkedEmails) {
        const excelDocument = await getAdminExcel({
          search: email,
          ...modalForm.getFieldsValue(true),
        });
        const link = document.createElement("a");
        link.href = excelDocument.file;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      setCheckedEmails([]);
      setData([]);
      await handleClick();
      setCheckAll(false);
      setIsModalOpen(false);
    } catch (e) {
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setCheckedEmails([]);
    setCheckAll(false);
  };

  const handleCheckAll = (e: any) => {
    setCheckAll(e.target.checked);
    setCheckedEmails(
      e.target.checked ? emails.map((admin: any) => admin.admin_id) : [],
    );
  };

  const onChange = (event: any) => {
    const { value, checked } = event.target;
    if (!checked) {
      let emailsClone = [...checkedEmails];
      emailsClone = emailsClone.filter((x) => x !== value);
      setCheckedEmails(emailsClone);
      setCheckAll(false);
      return;
    }
    setCheckedEmails([...checkedEmails, value]);
  };

  const filterCheckbox = (e: any) => {
    const { value } = e.target;
    setSearch(value);
    const result = emails.filter((admin) => {
      return admin.admin_name!.toLowerCase().startsWith(value.toLowerCase());
    });
    setEmailSearch(result);
  };

  return (
    <ContainerLayout title={title} showNotification={false}>
      <Col span={24}>
        <Form form={form} onFinish={handleClick}>
          <Space>
            <Form.Item name="startDate">
              <DatePicker />
            </Form.Item>
            <Form.Item name="endDate">
              <DatePicker />
            </Form.Item>
            <Form.Item name="search">
              <Input type="text" placeholder="Buscar por email" />
            </Form.Item>
            <Form.Item>
              <Button htmlType="submit" disabled={loading === true}>
                Filtrar
              </Button>
            </Form.Item>
            <Form.Item>
              <Button onClick={showModal} disabled={loading === true}>
                Generar reporte
              </Button>
            </Form.Item>
          </Space>
        </Form>
      </Col>
      <Col span={24} style={{ width: "100px", height: "83vh" }}>
        <Table
          style={{ minWidth: "100%" }}
          columns={columns}
          rowKey={(record) => record.id}
          dataSource={data}
          pagination={tableParams.pagination}
          loading={loading}
          onChange={handleTableChange}
          scroll={{ y: "calc(-250px + 97vh)" }}
        />
      </Col>
      <Modal
        title="Selecciona las compañias para generar los reportes"
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        confirmLoading={confirmLoading}
        okText={confirmLoading ? "Descargando" : "Ok"}
      >
        <Row gutter={[32, 16]}>
          <Col span={24}>
            <Form form={modalForm} onFinish={handleClickDate} layout="inline">
              <Form.Item name="startDate">
                <DatePicker />
              </Form.Item>
              <Form.Item name="endDate">
                <DatePicker />
              </Form.Item>
              <Button htmlType="submit" disabled={loading === true}>
                Filtrar
              </Button>
            </Form>
          </Col>
          <Col span={24}>
            <Input placeholder="Ingresar nombre" onChange={filterCheckbox} />
          </Col>
          <Col span={24}>
            <Checkbox checked={checkAll} onChange={handleCheckAll}>
              Seleccionar todos
            </Checkbox>
          </Col>
          <Col
            span={24}
            style={{
              maxHeight: "30vh",
              overflowX: "hidden",
              overflowY: "scroll",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {isModalLoading && (
              <Col className="loading-container error-container" span={24}>
                <LoadingOutlined />
              </Col>
            )}
            {!isModalLoading &&
              (!search.length
                ? emails.map((x) => (
                    <Checkbox
                      value={x.admin_id}
                      onChange={onChange}
                      checked={checkedEmails?.includes(x.admin_id!)}
                    >
                      {x.admin_name}
                    </Checkbox>
                  ))
                : emailSearch.map((x) => (
                    <Checkbox
                      value={x.admin_id}
                      onChange={onChange}
                      checked={checkedEmails?.includes(x.admin_id!)}
                    >
                      {x.admin_name}
                    </Checkbox>
                  )))}
          </Col>
        </Row>
      </Modal>
    </ContainerLayout>
  );
};

export default AdminReportDocumentList;
